import { useState } from 'react';
import { Copy, Check, Code } from 'lucide-react';

interface CodeSnippetProps {
  code: string;
  language?: 'html' | 'css' | 'javascript' | 'text';
  title?: string;
  maxLines?: number;
  showLineNumbers?: boolean;
  showCopyButton?: boolean;
}

export function CodeSnippet({
  code,
  language = 'html',
  title,
  maxLines,
  showLineNumbers = false,
  showCopyButton = true,
}: CodeSnippetProps) {
  const [copied, setCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const lines = code.split('\n');
  const shouldTruncate = maxLines !== undefined && lines.length > maxLines && !isExpanded;
  const displayedCode = shouldTruncate ? lines.slice(0, maxLines).join('\n') : code;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const getLanguageLabel = () => {
    switch (language) {
      case 'html':
        return 'HTML';
      case 'css':
        return 'CSS';
      case 'javascript':
        return 'JavaScript';
      default:
        return 'Text';
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
      {/* Header */}
      {(title || showCopyButton) && (
        <div className="flex items-center justify-between px-3 py-2 bg-gray-100 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Code size={14} className="text-gray-500" />
            <span className="text-caption font-medium text-gray-600">
              {title || getLanguageLabel()}
            </span>
          </div>
          {showCopyButton && (
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 px-2 py-1 text-caption text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors"
              aria-label={copied ? 'Copied!' : 'Copy code'}
            >
              {copied ? (
                <>
                  <Check size={14} className="text-status-success" />
                  <span className="text-status-success">Copied!</span>
                </>
              ) : (
                <>
                  <Copy size={14} />
                  <span>Copy</span>
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Code content */}
      <div className="overflow-x-auto">
        <pre className="p-3 text-body-sm font-mono text-gray-800 leading-relaxed">
          {showLineNumbers ? (
            <table className="w-full">
              <tbody>
                {displayedCode.split('\n').map((line, index) => (
                  <tr key={index} className="hover:bg-gray-100">
                    <td className="pr-4 text-right text-gray-400 select-none w-8">
                      {index + 1}
                    </td>
                    <td className="whitespace-pre-wrap break-all">{line || ' '}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <code className="whitespace-pre-wrap break-all">{displayedCode}</code>
          )}
        </pre>
      </div>

      {/* Show more/less button */}
      {maxLines !== undefined && lines.length > maxLines && (
        <div className="px-3 py-2 bg-gray-100 border-t border-gray-200">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-caption text-coral hover:text-coral/80 font-medium"
          >
            {isExpanded ? 'Show less' : `Show ${lines.length - maxLines} more lines`}
          </button>
        </div>
      )}
    </div>
  );
}

interface InlineCodeProps {
  children: string;
}

export function InlineCode({ children }: InlineCodeProps) {
  return (
    <code className="px-1.5 py-0.5 bg-gray-100 text-coral text-body-sm font-mono rounded">
      {children}
    </code>
  );
}

interface ElementSelectorProps {
  selector: string;
  showCopy?: boolean;
}

export function ElementSelector({ selector, showCopy = true }: ElementSelectorProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(selector);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy selector:', err);
    }
  };

  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-body-sm font-mono text-gray-700 max-w-full">
      <span className="truncate">{selector}</span>
      {showCopy && (
        <button
          onClick={handleCopy}
          className="flex-shrink-0 p-0.5 hover:bg-gray-200 rounded"
          aria-label={copied ? 'Copied!' : 'Copy selector'}
        >
          {copied ? (
            <Check size={12} className="text-status-success" />
          ) : (
            <Copy size={12} className="text-gray-400" />
          )}
        </button>
      )}
    </span>
  );
}
